import { useEffect, useRef, useState } from 'react';
import {
  useNotificacionesComentarios,
  type NotificacionComentario,
} from '../hooks/useNotificacionesComentarios';

/**
 * Botón de notificaciones para topbar:
 *   - Ícono campana con badge de count.
 *   - Click abre dropdown con lista de comentarios sin leer agrupada por
 *     parcela. Click en una notificación dispara `onSelect(parcela_codigo)`
 *     — el host (Dashboard / equipo) decide cómo navegar y abrir el panel.
 *   - Realtime via useNotificacionesComentarios (sub a Postgres changes
 *     + polling fallback 60s + refresh on visibilitychange).
 */
interface Props {
  /**
   * Llamado al click en una notificación. El host abre el panel de
   * comentarios en esa parcela. Recibe la notificación completa para
   * que el host pueda construir el título legible (parcela_num + codigo).
   */
  onSelect: (item: NotificacionComentario) => void;
}

export function NotificacionesDropdown({ onSelect }: Props) {
  const { notifications, loading } = useNotificacionesComentarios();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Click fuera cierra el dropdown.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Cerrar con ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const count = notifications.length;

  return (
    <div className="notif-dropdown" ref={wrapRef}>
      <button
        type="button"
        className={`notif-dropdown__bell${count > 0 ? ' has-unread' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={count > 0 ? `${count} notificación(es) sin leer` : 'Notificaciones'}
        aria-label="Notificaciones"
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width={15} height={15} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {count > 0 && (
          <span className="notif-dropdown__count">{count > 99 ? '99+' : count}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown__panel" role="dialog" aria-label="Notificaciones">
          <header className="notif-dropdown__header">
            <span className="notif-dropdown__title">Mensajes sin leer</span>
            {count > 0 && <span className="notif-dropdown__pill">{count}</span>}
          </header>
          <ul className="notif-dropdown__list">
            {loading && notifications.length === 0 ? (
              <li className="notif-dropdown__empty">Cargando…</li>
            ) : notifications.length === 0 ? (
              <li className="notif-dropdown__empty">Sin mensajes nuevos.</li>
            ) : (
              notifications.map((n) => (
                <NotifItem
                  key={n.id}
                  item={n}
                  onClick={() => {
                    if (n.parcela_codigo) onSelect(n);
                    setOpen(false);
                  }}
                />
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function NotifItem({ item, onClick }: { item: NotificacionComentario; onClick: () => void }) {
  const autorLabel = item.autor_nombre ?? labelByRol(item.autor_rol);
  const parcelaLabel = item.parcela_num != null ? `Parcela ${item.parcela_num}` : item.parcela_codigo ?? 'Parcela';
  const isEstado = item.type === 'estado_cambio';
  return (
    <li className="notif-dropdown__item">
      <button type="button" className="notif-dropdown__item-btn" onClick={onClick}>
        <div className="notif-dropdown__item-head">
          {isEstado && (
            <span
              aria-label="Cambio de estado"
              title="Cambio de estado de la gestión"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 16, height: 16, borderRadius: 4, marginRight: 4,
                background: 'rgba(180, 201, 111, 0.18)', color: 'var(--btn-primary-bg, #b4c96f)',
                flexShrink: 0,
              }}
            >
              <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            </span>
          )}
          <span className="notif-dropdown__item-autor">{autorLabel}</span>
          <span className="notif-dropdown__item-dot" />
          <span className="notif-dropdown__item-parcela">{parcelaLabel}</span>
          <span className="notif-dropdown__item-time">{formatTime(item.created_at)}</span>
        </div>
        <p className="notif-dropdown__item-snippet">
          {isEstado && item.estado_new ? (
            <>
              {item.cuerpo.replace(/^Cambio de estado: /, '')}
            </>
          ) : (
            truncate(item.cuerpo, 110)
          )}
        </p>
      </button>
    </li>
  );
}

function labelByRol(rol: string | null): string {
  switch (rol) {
    case 'cliente_elawan': return 'Cliente Elawan';
    case 'tecnico_aosenuma': return 'Aosenuma · Técnico';
    case 'admin_aosenuma':   return 'Aosenuma · Admin';
    case 'auditor':          return 'Auditor';
    default:                 return 'Usuario';
  }
}

function formatTime(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  const diffMs = Date.now() - t.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  if (d === 1) return 'ayer';
  if (d < 7) return `${d} d`;
  return t.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n).trim() + '…' : s;
}
